// src/oura/audit.ts
//
// Mock SEO/AEO audit dataset per business. This is the shape the FE builds
// against while the audit.checks[] BE extension is negotiated (per-scan
// tracker coverage: crawl issues #7, CWV #8, schema #10/#16, labels #12,
// 301s/linking #13-14, robots/sitemap #15, re-verify #18, naming baseline #5).
// Deterministic per business so the demo is stable.

import type { Business, QuestionResult, ScoreSnapshot } from "./contract";
import { pageUrlKey } from "./lib/oura";
import { getSearchPerformance } from "@service/aeo/devMock";
import type {
    AeoCrawlAbsence, AeoReadabilityAbsence, AeoReadabilityComponent, AeoReadabilityCrawl, AeoSourceDomain,
    AeoTrafficInsights, AeoVisibilityCoverage, PageKind,
} from "@service/aeo";

export type IssueSeverity = "error" | "warning" | "notice";

/** A page an issue was detected on (drives the expand-in-place page list). */
export interface IssuePage {
    url: string;
    title: string;
    discovered: string; // ISO datetime, e.g. "2026-07-21T14:30"
    meta: string; // the issue-specific column value (status code, hops, word count…)
    /**
     * What a person said about this page and this check (ourteam-backend#4530).
     *
     * Separate from the crawl's own verdict, which is what put the page in this
     * list. A fix made by hand in WordPress is invisible to the crawl until it
     * runs again, and until this existed there was nowhere to record it.
     *
     * `null` means nobody has said anything — not the same as `not_started`.
     */
    operatorStatus?: "not_started" | "in_progress" | "done" | null;
    operatorStatusAt?: string | null;
    operatorStatusBy?: string | null;
    /** Marked done, and the last crawl still flags it. Shown, never resolved:
     *  reverting calls the operator wrong, keeping it calls the crawl wrong,
     *  and only one of those is checkable afterwards. */
    statusDisagreement?: "still_failing" | null;
    /** Where on the page the problem is, for checks no draft can fix (#4529). */
    locations?: IssuePageLocation[];
    /** `broken_outbound` only: the destinations that no longer resolve. */
    deadLinks?: IssueDeadLink[];
}

/** One offending element on an affected page (ourteam-backend#4529 §2). */
export interface IssuePageLocation {
    element: string;
    src?: string | null;
    text?: string | null;
    href?: string | null;
}

/** A dead outbound link, with the words on the page that carry it (#4514). */
export interface IssueDeadLink {
    url: string;
    reason?: string | null;
    status?: number | null;
    finalUrl?: string | null;
    anchor?: string | null;
}

export interface AuditIssue {
    key: string;
    label: string;
    severity: IssueSeverity;
    count: number;
    desc: string;
    tracker: string;
    about: string; // "About the issue" — shown in the How-to-fix drawer
    fix: string[]; // "How to fix" — remediation steps in the drawer
    pageColLabel: string; // header for the issue-specific page column (Semrush-style)
    pages: IssuePage[]; // affected pages, revealed when the row is expanded
    /**
     * The draft types that can actually resolve THIS finding, from the backend
     * (ourteam-backend#4529 §1).
     *
     * `[]` means no draft resolves it and the control is hidden — a real answer,
     * not a missing one. `undefined` means the payload predates the field, and
     * the caller falls back to its own map rather than showing nothing.
     */
    draftTypes?: string[];
    /** True when the answer is WHERE the problem is rather than what to write;
     *  those pages carry `locations`. */
    locates?: boolean;
}

// Core Web Vitals / PageSpeed. Shaped like a PageSpeed Insights (Lighthouse +
// CrUX) result so the BE swap is a one-file change: PSI v5 is proxied through
// the BFF (the API key never ships in the client bundle), mobile + desktop.
export type CwvVerdict = "good" | "needs-improvement" | "poor" | "insufficient";

export interface PsOpportunity {
    label: string;
    savingMs: number; // estimated load-time saving
    /** Lighthouse audit id (#4491): the stable key for the plain-English line. */
    id?: string;
}

/**
 * One PSI run. **Two different measurements live in here and they are not
 * interchangeable** — mixing them is what made Oura's numbers look like they
 * disagreed with pagespeed.web.dev when they did not:
 *
 *   LAB   — `performance` and every *Ms/`cls` below except `inpMs`. One
 *           simulated load on Google's hardware, at this instant. Google's own
 *           report says "values are estimated and may vary": two runs minutes
 *           apart routinely differ, and on a script-heavy site TBT alone can
 *           swing 15x and drag the score with it.
 *   FIELD — `inpMs`, `fieldData`, `fieldPass`. Real Chrome visitors, 75th
 *           percentile over a trailing 28 days, refreshed once a day. Stable
 *           within a day, and what pagespeed.web.dev leads its report with.
 *
 * Every numeric metric is nullable because "PSI did not return this" is a real
 * outcome that must not render as a measurement. The BE still sends 0 for a
 * missing value today (see the FE→BE handoff); `notMeasured()` in SiteAudit.tsx
 * carries that case until it sends null.
 */
export interface PageSpeedRun {
    strategy: "mobile" | "desktop";
    performance: number | null; // 0-100 Lighthouse LAB performance score
    // ── Lab (Lighthouse) ───────────────────────────────────────────────────
    lcpMs: number | null; // Largest Contentful Paint
    fcpMs: number | null; // First Contentful Paint
    cls: number | null; // Cumulative Layout Shift (unitless)
    tbtMs: number | null; // Total Blocking Time
    siMs: number | null; // Speed Index
    ttiMs: number | null; // Time to Interactive
    // ── Field (CrUX) ───────────────────────────────────────────────────────
    /** Interaction to Next Paint. **Field, not lab** — Lighthouse cannot
     *  measure INP at all, so the BE reads it from CrUX (`_field_num` in
     *  aeo_pagespeed.py). It spent this card's whole life in the lab metric
     *  grid, where a missing value rendered as a green "0 ms" core vital. */
    inpMs: number | null;
    /** Lighthouse's `server-response-time` audit, falling back to the CrUX
     *  TTFB percentile. NOT the same quantity as the TTFB PSI shows under
     *  field data, which also counts DNS, connect and redirects — so it reads
     *  systematically lower. Labelled accordingly wherever it is shown. */
    ttfbMs: number | null;
    fieldData: CwvVerdict; // CrUX field verdict (real-user data)
    fieldPass: boolean; // Core Web Vitals Assessment (real-user pass/fail)
    opportunities: PsOpportunity[]; // top Lighthouse opportunities
    /** The other three Lighthouse categories (ourteam-backend#3957). The BE has
     *  sent all three since #3957 and this type never declared them, so they
     *  were dropped on the floor exactly the way `screenshot` was — PSI shows
     *  them next to Performance and Oura showed none of them. `null` = PSI did
     *  not return that category, never a fabricated 0. */
    accessibility?: number | null;
    bestPractices?: number | null;
    seo?: number | null;
    /** Lighthouse's `final-screenshot` — the page as actually rendered at this
     *  viewport, as a base64 data URI. The fixtures have none (there is no real
     *  page to shoot), so the device panel falls back to its stylised frame. */
    screenshot?: string | null;
    /** When THIS strategy was measured (ourteam-backend#4470). A refresh where
     *  one strategy's PSI call failed keeps that strategy's last good run — so
     *  under the block-level `capturedAt` alone, a week-old desktop run was
     *  presented as "just now". Optional: a payload stored before #4470 has no
     *  per-run stamp and falls back to the block's. */
    capturedAt?: string | null;
    /** How many PSI calls this run is the median of (#4492). Absent on older
     *  payloads, where the card cannot say how many tests stand behind it. */
    samples?: number;
    /** Failed opportunities before the list was cut (#4491). */
    opportunitiesTotal?: number;
}

export interface PageSpeed {
    capturedAt: string;
    runs: PageSpeedRun[]; // mobile + desktop
    /** Each row is a `_parse_run()` of that page's own PSI call, so both numbers
     *  became nullable with ourteam-backend#4470: a page PSI could not score
     *  sends `null`, not a fabricated 0. While these were typed non-null,
     *  `secs(null)` printed "LCP 0.0 s" and the score rendered blank in red —
     *  the same defect #4470 removed from `runs[]`, still alive in this list. */
    pages: { url: string; title: string; performance: number | null; lcpMs: number | null }[];
    /** When `pages[]` was captured (ourteam-backend#4470). Re-scan does NOT
     *  re-test these — `refresh_pagespeed()` carries the rows over verbatim — so
     *  they keep their own stamp instead of riding the block's. Optional: older
     *  payloads have none, and the card then says which scan they belong to
     *  rather than inventing a date. */
    pagesCapturedAt?: string | null;
    /** Key pages the business has before the list was cut to those tested (#4491). */
    pagesTotal?: number;
}

export interface PriorityPage {
    url: string;
    title: string;
    visits: number;
    note: string;
    /** Generated indexes are excluded from this list upstream by the BE
     *  (ourteam-backend#4097), but the field is present on every entry so the FE
     *  filters on ONE field everywhere rather than trusting a list to be clean. */
    pageKind?: PageKind;
}

export interface UnderperformingPage {
    url: string;
    title: string;
    reason: "Orphan page" | "404" | "Thin content" | "No internal links";
    visits: number;
    /** Labelled here rather than dropped — a broken category page is a real crawl
     *  finding, it just has no copy to improve (ourteam-backend#4097). */
    pageKind?: PageKind;
}

export interface Recommendation {
    id: string;
    title: string;
    impact: string;
    priority: "high" | "medium" | "low";
    effort: "Easy" | "Medium" | "Hard";
    horizon: "Quick win" | "Long term";
    /**
     * What the plan says about this finding (ourteam-backend#4528).
     *
     * The two surfaces used to disagree about the same work: a task marked done
     * in the Tracker still read as outstanding here, because the cards were
     * generated per failing check and never looked at the plan.
     *
     * `null` means no task covers this check, which the card must not dress up
     * as `not_started`. Fixtures carry none of these at all.
     */
    workState?: "not_started" | "in_progress" | "blocked" | "done" | null;
    workStateLabel?: string | null;
    /** Needs no more attention. Never a reason to remove the card: the finding
     *  is still measurably present until the next crawl says otherwise. */
    dim?: boolean;
    /**
     * The deliverable with an item for this card's check (H13), when a signed
     * contract covers it. `null`: outside the contract.
     */
    code?: string | null;
    deliverableKey?: string | null;
}

export interface CompetitorRow {
    name: string;
    domain: string;
    /** `null` = not scored in this run. */
    aeo: number | null;
    /** `null` = not measured. See AeoBusinessCompetitor.seo — a competitor's
     *  search performance is not something we measure today. */
    seo: number | null;
    /** Questions this rival was named on. `null` = not scored in this run. */
    queries: number | null;
    note: string;
}

export interface NamingEntry {
    engine: "ChatGPT" | "Gemini" | "Perplexity";
    summary: string;
    mentions: string[];
}

export interface BusinessAudit {
    /** Technical hygiene, not search performance — the BE retired the `seoScore`
     *  name for exactly that reason (ourteam-backend#3818). In the fixtures this
     *  is a pillar-derived score and `siteHealth` below is the crawl-error
     *  percentage, so the two differ; on a live business the BE has one number
     *  and both carry it. */
    siteHealthScore: number;
    /** Live only — absent on fixtures. See AeoBusinessAudit. */
    siteHealthScoreAbsent?: AeoCrawlAbsence | null;
    siteHealthReadablePages?: number | null;
    /** The third gauge (#3765). Live only — the whole group is absent on
     *  fixtures and on runs finalized before ourteam-backend#4877. */
    aiReadabilityScore?: number | null;
    aiReadabilityVerdict?: string | null;
    aiReadabilityAbsent?: AeoReadabilityAbsence | null;
    aiReadabilityComponents?: AeoReadabilityComponent[];
    /** Set only when the readability block was measured AFTER the scan around
     *  it (#3831). Absent means the scan's own date. */
    aiReadabilityMeasuredAt?: string | null;
    /** What the crawl behind that block found out about itself (#3831). */
    aiReadabilityCrawl?: AeoReadabilityCrawl | null;
    /** Why `aeoScore` is absent, when it is. Live only. */
    aeoScoreAbsent?: string | null;
    /** `null` when the scan produced no score; `aeoScoreAbsent` says why. */
    aeoScore: number | null;
    siteHealthVerdict: string;
    aeoVerdict: string;
    siteHealth: number;
    pagesCrawled: number;
    /** Every address the crawl knew about — read, queued, or in the sitemap.
     *  Stable between runs by design (unlike `truncation.unvisited`, which
     *  counts the live crawl queue and must never be used as a denominator).
     *  `null` on fixtures and on runs finalized before the BE sent it. */
    pagesKnown?: number | null;
    /** The crawl stopped before reading every known address (page ceiling or
     *  time budget), so `pagesCrawled < pagesKnown` is by design, not a bug. */
    truncated?: boolean;
    /** WHICH of those two stopped it. The distinction was assumed rather than
     *  carried until ourteam-backend#4569 raised the page ceiling: crawls then
     *  started ending on the time budget while the UI still blamed a "scan
     *  limit", pointing operators at a lever that had already been moved.
     *  `undefined` on fixtures and on runs finalized before the BE sent it. */
    truncationReason?: "max_pages" | "time_budget";
    errors: number;
    warnings: number;
    issues: AuditIssue[];
    strengths: string[];
    weaknesses: string[];
    priorityPages: PriorityPage[];
    underperforming: UnderperformingPage[];
    /** **Absent, not zeroed**, when no analytics connector supplied it. The BE
     *  omits the key entirely rather than sending zeros (`_traffic()` returns
     *  None), and that distinction has to survive: a zero here is a measured
     *  "nobody visits", which is a different and usually wrong claim. Note the
     *  source is **GA4**, not Search Console — Search Console has no session or
     *  bounce concept. */
    traffic?: AeoTrafficInsights & {
        visits: number;
        unique: number;
        pagesPerVisit: number;
        /** A percent (45.2), already converted from GA4's fraction. */
        bounceRate: number;
        /** `null` when not measured. A number here is a real comparison. */
        deltaPct: number | null;
        /** `[]` when there is no trend yet — which is NOT "not connected". */
        trend: number[];
        /** The window `visits` covers. Absent on fixtures. */
        window?: { start: string; end: string; days: number } | null;
        /** One series per granularity (ourteam-backend#5018). Absent until it ships. */
        series?: {
            daily?: { date: string; sessions: number }[] | null;
            weekly?: { start: string; sessions: number }[] | null;
            monthly?: { start: string; sessions: number }[] | null;
        } | null;
    };
    /** GA4 connected, read, and NO sessions recorded in this window
     *  (ourteam-backend#6749): measured, unlike an absent `traffic`. Null when
     *  not known, which is every older payload. */
    trafficEmpty?: { start: string; end: string; days: number } | null;
    /** Measured per-area health from the crawl. Absent on fixtures and on runs
     *  finalized before the BE emitted it — the card renders a "not measured"
     *  state rather than deriving numbers from the overall score. */
    healthByArea?: {
        crawlability: number;
        internalLinking: number;
        markup: "present" | "missing";
        robotsTxt: "found" | "not_found";
    };
    recommendations: Recommendation[];
    competitors: CompetitorRow[];
    naming: { capturedAt: string; entries: NamingEntry[] };
    pageSpeed: PageSpeed;
    /**
     * The crawl inventory (ourteam-backend#3957) — every page the crawler read,
     * each carrying the audit checks it failed.
     *
     * The Pages tab used `underperforming[]` for this, which is the BE's
     * three-check, twelve-row summary. This is all eleven checks across every
     * crawled page, and it is what lets one page say everything wrong with it
     * instead of the one or two things a summary had room for.
     *
     * Absent on fixtures and on runs finalized before it existed — never
     * substituted with an empty array, because "we did not crawl" and "we
     * crawled and everything passed" are opposite claims.
     */
    crawledPages?: CrawledPageRow[];
    /** When the crawl behind these numbers ran: `results.audit.generated_at`,
     *  or the scan's own date on a payload stored before it was sent. */
    crawledAt?: string | null;
    /**
     * The crawl did not run, or read no page at all (`mode: "visibility_only"`
     * or 0 pages). The backend still sends a zero-filled block then, and read
     * as measured it would accuse the site of failures nobody measured.
     */
    crawlFailed?: boolean;
    /** The backend's own sentence for why (`results.audit.mode_reason`). */
    crawlFailedReason?: string | null;
    /**
     * Site Health on the previous scan, and whether both were scored by the
     * same set of checks. No live source yet: the score is a weighted share
     * over every catalogue check, so a new check moves it with no change to
     * the site, and nothing on the wire says which catalogue scored a scan.
     * Fixture-only until ourteam-backend handoff "Site Health method stamp
     * for deltas"; the tile shows a change only when `sameMethod` is true.
     */
    previousSiteHealth?: { score: number; at: string; sameMethod: boolean } | null;
}

/** One page of the crawl inventory, as the FE reads it. */
export interface CrawledPageRow {
    url: string;
    /** The page's own <title>. **Empty means the crawler read the page and
     *  found none** — a measurement, not a gap in the payload, which is what
     *  makes "this page has no title" something we can say out loud rather
     *  than guess at. */
    title: string;
    /** The audit check keys this page failed — joins to `issues[].key`. */
    failed: string[];
    status?: number;
    /** So a PDF or an image is never accused of missing a page title. */
    contentType?: string;
    pageKind?: PageKind;
}

function hashOf(s: string): number {
    return s.split("").reduce((a, c) => (a * 31 + c.charCodeAt(0)) % 9973, 7);
}

// A small pool of realistic crawl paths. An issue's affected pages are drawn
// from here deterministically so the demo is stable (a page can carry several
// issues, exactly as a real crawl reports).
const CRAWL_PATHS = [
    "/", "/services", "/about", "/faqs", "/pricing", "/contact",
    "/blog/opening-post", "/locations", "/team", "/promo-2024", "/gallery", "/reviews", "/booking",
];

function titleFromPath(path: string): string {
    if (path === "/") return "Homepage";
    const seg = path.split("/").filter(Boolean).pop() ?? "";
    return seg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * The backend's count-aware words for a weakness (`_WEAKNESS_COPY` in
 * aeo_overview.py), for the checks the samples carry: `{n}` is the count and
 * `{s}` the plural ending. A sample weakness reads exactly as a live one.
 */
const WEAKNESS_COPY: Record<string, string> = {
    broken_4xx: "{n} page{s} return a 4xx error",
    orphan_pages: "{n} orphan page{s} with no internal links",
    no_schema: "{n} page{s} without structured data",
    robots_missing: "No robots.txt at the site root",
    missing_meta: "{n} page{s} missing a meta description",
    dup_titles: "{n} page{s} share a duplicate title",
    redirect_chains: "{n} URL{s} redirect more than once",
    canonicals: "{n} page{s} missing a canonical tag",
    thin_content: "{n} page{s} with thin content",
    missing_alt: "{n} page{s} with images missing alt text",
    cwv: "{n} page{s} fail Core Web Vitals",
};

/**
 * The backend's weaknesses rule (`_weaknesses`): the checks that found
 * something, errors first, then the most pages, at most five.
 *
 * fixture-only until ourteam-backend handoff: Structured strengths and
 * weaknesses. Each item also carries a line, the check's own description, the
 * way the structured items will; the card splits the two at the spaced dash.
 */
function weaknessesOf(issues: AuditIssue[]): string[] {
    const order: IssueSeverity[] = ["error", "warning", "notice"];
    return issues
        .filter((i) => i.count > 0 && WEAKNESS_COPY[i.key])
        .sort((a, b) => order.indexOf(a.severity) - order.indexOf(b.severity) || b.count - a.count)
        .slice(0, 5)
        .map((i) => {
            const title = WEAKNESS_COPY[i.key].replace("{n}", String(i.count)).replace("{s}", i.count === 1 ? "" : "s");
            return `${title} \u2013 ${i.desc}`;
        });
}

// The Semrush-style per-issue column: each check surfaces the one extra fact
// that matters for it, with a small pool of deterministic sample values.
const ISSUE_COL: Record<string, { label: string; values: string[] }> = {
    broken_4xx: { label: "Status code", values: ["404", "410", "500", "403"] },
    orphan_pages: { label: "Internal links", values: ["0"] },
    no_schema: { label: "Missing type", values: ["LocalBusiness", "FAQPage", "Organization", "Product", "Review"] },
    robots_missing: { label: "Expected at", values: ["/robots.txt"] },
    missing_meta: { label: "Current meta", values: ["—"] },
    dup_titles: { label: "Duplicate of", values: ["/services", "/about", "/pricing"] },
    redirect_chains: { label: "Redirect hops", values: ["3", "2", "4"] },
    canonicals: { label: "Canonical", values: ["None"] },
    thin_content: { label: "Word count", values: ["84", "62", "103", "45", "118"] },
    missing_alt: { label: "Images", values: ["6", "3", "9", "12", "4"] },
    cwv: { label: "LCP", values: ["5.2 s", "4.7 s", "6.1 s"] },
};

/** An ISO date `days` before a fixture's date-only `capturedAt`, in UTC so the
 *  result does not shift with the viewer's time zone. */
function daysBefore(isoDate: string, days: number): string {
    const [y, m, d] = isoDate.slice(0, 10).split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d - days)).toISOString().slice(0, 10);
}

/**
 * A fixture's Google Analytics block, dated the way `ga4_windows` dates a real
 * one (ourteam-backend#5018): the window is the 28 days to the day before the
 * scan, the weeks are 12 whole weeks to the last Sunday, and the months are 12
 * calendar months whose newest is the month so far. Every figure is summed
 * from one run of daily readings, so the total, the days, the weeks and the
 * months agree with each other.
 *
 * fixture-only: a live block carries the same window and series from GA4.
 */
function fixtureTraffic(h: number, capturedAt: string): NonNullable<BusinessAudit["traffic"]> {
    const end = daysBefore(capturedAt, 1);
    // Enough days to hold 12 whole calendar months behind the newest one.
    const DAYS = 400;
    const daily = Array.from({ length: DAYS }, (_, i) => {
        const date = daysBefore(end, DAYS - 1 - i);
        const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
        // A slow climb, busier weekends, a quieter Monday, never a zero day.
        const rhythm = weekday === 0 || weekday === 6 ? 1.35 : weekday === 1 ? 0.8 : 1;
        const sessions = Math.max(1, Math.round((12 + (h % 16) + i / 40) * rhythm + ((h + i * 37) % 9) - 4));
        return { date, sessions };
    });
    const sum = (rows: Array<{ sessions: number }>) => rows.reduce((n, r) => n + r.sessions, 0);
    const current = daily.slice(-28);
    const visits = sum(current);
    const previous = sum(daily.slice(-56, -28));
    const lastSunday = DAYS - 1 - new Date(`${end}T00:00:00Z`).getUTCDay();
    const weekly = Array.from({ length: 12 }, (_, k) => {
        const from = lastSunday - 6 - 7 * (11 - k);
        return { start: daily[from].date, sessions: sum(daily.slice(from, from + 7)) };
    });
    const months = Array.from(new Set(daily.map((d) => d.date.slice(0, 7)))).slice(-12);
    const monthly = months.map((ym) => ({ start: `${ym}-01`, sessions: sum(daily.filter((d) => d.date.startsWith(ym))) }));
    return {
        visits,
        unique: Math.round(visits * 0.78),
        pagesPerVisit: 2 + ((h % 14) / 10),
        bounceRate: 38 + (h % 18),
        deltaPct: Math.round(((visits - previous) / previous) * 1000) / 10,
        trend: weekly.map((w) => w.sessions),
        window: { start: current[0].date, end, days: 28 },
        series: { daily: current, weekly, monthly },
    };
}

function pillarPct(snapshot: ScoreSnapshot, key: string, fallback: number): number {
    const p = snapshot.pillars.find((x) => x.key === key);
    if (!p || p.max === 0) return fallback;
    return Math.max(8, Math.min(96, Math.round((p.score / p.max) * 100)));
}

function verdictFor(score: number): string {
    if (score >= 70) return "Strong, protect the lead";
    if (score >= 50) return "Moderate, fixable";
    return "Weak, high upside";
}

/** Site Health's verdict in the backend's own words (`_band(score, "seo")` in aeo_overview.py). */
function siteHealthVerdictFor(score: number): string {
    if (score >= 75) return "Strong SEO fundamentals, keep protecting it";
    if (score >= 50) return "Developing SEO fundamentals, clear upside";
    if (score >= 25) return "Weak SEO fundamentals, high upside";
    return "Critical gap in SEO fundamentals, major upside";
}

export function getAudit(business: Business, snapshot: ScoreSnapshot): BusinessAudit {
    const h = hashOf(business.id);
    const siteHealthScore = pillarPct(snapshot, "search_rank", 40 + (h % 25));
    const aeoScore = pillarPct(snapshot, "answer_presence", 25 + (h % 20));
    const pagesCrawled = 32 + (h % 30);

    const d = business.domain;
    // Up to 12 sample pages per issue — enough that a 25-page issue clearly
    // needs the "view all" sub-view rather than dumping every row inline.
    const pagesFor = (key: string, count: number, values: string[]): IssuePage[] => {
        const hk = hashOf(key);
        const n = Math.min(count, 12);
        // The homepage is where every internal link starts, so it is never an
        // orphan, and a crawl that read the site did not get a 4xx from it.
        const pool = key === "orphan_pages" || key === "broken_4xx" ? CRAWL_PATHS.filter((p) => p !== "/") : CRAWL_PATHS;
        return Array.from({ length: n }, (_, i) => {
            const p = pool[(hk + i * 3) % pool.length];
            const mins = (hk + i * 37) % (10 * 60); // within a 10h working window from 09:00
            const hh = String(9 + Math.floor(mins / 60)).padStart(2, "0");
            const mm = String(mins % 60).padStart(2, "0");
            return {
                url: `${d}${p}`,
                title: titleFromPath(p),
                discovered: `${snapshot.capturedAt}T${hh}:${mm}`,
                meta: values[(hk + i) % values.length],
            };
        });
    };

    const issuesBase: Array<Omit<AuditIssue, "pages" | "pageColLabel">> = [
        { key: "broken_4xx", label: "Pages returning 4XX status codes", severity: "error", count: 2 + (h % 3), desc: "Broken destinations that waste crawl budget and dead-end visitors.", tracker: "#13", about: "These URLs return a 4XX error, so crawlers and visitors hit a dead end. They waste crawl budget and any links pointing at them leak equity into nothing.", fix: ["Restore the page, or 301-redirect the URL to the closest live equivalent.", "Update or remove the internal links that still point at the broken URL.", "Re-submit the sitemap so engines recrawl the corrected paths."] },
        { key: "orphan_pages", label: "Orphan pages with no internal links", severity: "error", count: 3 + (h % 2), desc: "Pages crawlers and visitors cannot reach from anywhere on the site.", tracker: "#14", about: "These pages exist but nothing on the site links to them, so crawlers and visitors can only reach them by luck. Crawl paths and link equity never flow to them.", fix: ["Add internal links from relevant hub, category or navigation pages.", "Include them in a related-links block where it makes sense.", "If a page is obsolete, 301-redirect it and drop it from the sitemap."] },
        { key: "no_schema", label: "Pages missing structured data", severity: "error", count: Math.max(6, pagesCrawled - 8), desc: "No LocalBusiness, FAQPage or Organization markup for engines to cite.", tracker: "#10 #16", about: "None of these pages carry LocalBusiness, FAQPage or Organization structured data, so AI engines and search have no machine-readable facts to cite about the business.", fix: ["Add Organization + LocalBusiness markup to the homepage and key templates.", "Add FAQPage markup wherever real Q&A content already exists.", "Validate every page with the Rich Results Test before publishing."] },
        { key: "robots_missing", label: "robots.txt not found", severity: "error", count: 1, desc: "The sitemap is never declared to crawlers, slowing discovery.", tracker: "#15", about: "No robots.txt was found at the domain root, so the XML sitemap is never declared to crawlers and discovery of new or updated pages is slower than it should be.", fix: ["Publish a robots.txt at the domain root.", "Declare the sitemap location with a Sitemap: directive.", "Confirm no important path is accidentally disallowed."] },
        { key: "missing_meta", label: "Pages without meta descriptions", severity: "warning", count: 5 + (h % 4), desc: "Search and AI previews fall back to arbitrary page text.", tracker: "#12", about: "These pages have no meta description, so search and AI previews fall back to arbitrary on-page text, often the wrong sentence entirely.", fix: ["Write a unique, benefit led description of 120 to 155 characters per page.", "Lead with the primary query the page answers.", "Never reuse the same description across pages."] },
        { key: "dup_titles", label: "Duplicate or missing title tags", severity: "warning", count: 3 + (h % 3), desc: "Pages compete with each other for the same query.", tracker: "#12", about: "Two or more pages share the same (or a missing) title tag, so they compete with each other for the same query and dilute each other's relevance.", fix: ["Give every page a unique, descriptive title.", "Put the primary term first and the brand last.", "Fix or consolidate the pages that duplicate an existing title."] },
        { key: "redirect_chains", label: "Redirect chains", severity: "warning", count: 1 + (h % 3), desc: "Multi-hop redirects that dilute signals and slow crawls.", tracker: "#13", about: "These URLs redirect more than once before resolving. Each hop dilutes signals and slows both crawlers and users.", fix: ["Point every redirect straight at the final destination in one hop.", "Update internal links to use the final URL directly.", "Remove redirects that are no longer needed."] },
        { key: "canonicals", label: "Pages without canonical tags", severity: "warning", count: Math.max(4, pagesCrawled - 14), desc: "Exposed to duplicate URL and tracking parameter dilution.", tracker: "#7", about: "These pages declare no canonical tag, leaving them exposed to duplicate-URL and tracking-parameter dilution across the site.", fix: ["Add a self-referencing canonical to each page.", "Point parameterised or duplicate URLs at the canonical version.", "Confirm the canonical target returns 200 and is indexable."] },
        { key: "thin_content", label: "Thin content pages", severity: "warning", count: 4 + (h % 3), desc: "Under 120 words of body copy, too little to answer anything.", tracker: "#7", about: "These pages carry under 120 words of body copy, too little for search or AI to understand what they answer.", fix: ["Expand each page to genuinely answer its topic, past 300 words where appropriate.", "Fold near empty pages into a stronger parent page and redirect.", "Add real value, not filler."] },
        { key: "missing_alt", label: "Images missing alt text", severity: "notice", count: 9 + (h % 8), desc: "Accessibility and image search signals left on the table.", tracker: "#7", about: "Images on these pages have no alt text, forfeiting accessibility and image-search signals.", fix: ["Add descriptive alt text to every meaningful image.", "Describe the content, not 'image of'.", "Leave purely decorative images with an empty alt attribute."] },
        { key: "cwv", label: "Slow LCP on key templates", severity: "notice", count: 2 + (h % 2), desc: "Large unoptimized images push Core Web Vitals into the amber zone.", tracker: "#8", about: "Large, unoptimised images push Largest Contentful Paint into the amber/red zone on key templates, hurting Core Web Vitals and the mobile first impression.", fix: ["Convert hero and above-the-fold images to WebP/AVIF and compress them.", "Set explicit width/height and lazy-load below-the-fold media.", "Preload the LCP image and defer render-blocking scripts."] },
    ];
    const issues: AuditIssue[] = issuesBase.map((iss) => {
        const col = ISSUE_COL[iss.key];
        return { ...iss, pageColLabel: col.label, pages: pagesFor(iss.key, iss.count, col.values) };
    });
    const errors = issues.filter((i) => i.severity === "error").reduce((n, i) => n + i.count, 0);
    const warnings = issues.filter((i) => i.severity === "warning").reduce((n, i) => n + i.count, 0);
    const siteHealth = Math.max(35, Math.min(92, 100 - Math.round(errors * 2.4 + warnings * 0.7)));

    const svc = business.vertical.toLowerCase();
    const issueCount = (key: string) => issues.find((i) => i.key === key)?.count ?? 0;

    // fixture-only: the sample's Search Console pages, the block the Search
    // screen shows, so a page's clicks read the same on Site and on Search.
    // Joined on the whole address, so a sample on another domain gets none.
    const search = getSearchPerformance(business.id);
    const clicksByKey = new Map((search.pages ?? []).map((p) => [pageUrlKey(p.url), p.clicks]));
    const clicksOf = (url: string) => clicksByKey.get(pageUrlKey(url)) ?? 0;

    // Never the homepage, as live (`_pick_priority_pages`): it has its own
    // PageSpeed run, and these are the other pages worth testing.
    const priorityPages: PriorityPage[] = [
        { url: `${d}/services`, title: "Services", note: "Best converting page, no schema yet" },
        { url: `${d}/locations/${business.city.toLowerCase().replace(/\s+/g, "-")}`, title: `${business.city} location`, note: "Map pack candidate once LocalBusiness ships" },
        { url: `${d}/faqs`, title: "FAQs", note: "22 real questions, zero FAQPage markup" },
        { url: `${d}/pricing`, title: "Pricing", note: "High intent, thin on-page copy" },
    ].map((p) => ({ ...p, visits: clicksOf(p.url) }));
    const underperforming: UnderperformingPage[] = [
        { url: `${d}/blog/opening-post`, title: "Old blog post", reason: "Orphan page", visits: 2 },
        { url: `${d}/mat-training`, title: "Retired service page", reason: "404", visits: 0 },
        { url: `${d}/promo-2024`, title: "Expired promotion", reason: "No internal links", visits: 1 },
        { url: `${d}/team`, title: "Team page", reason: "Thin content", visits: 6 },
    ];

    const traffic = fixtureTraffic(h, snapshot.capturedAt);

    const recommendations: Recommendation[] = [
        { id: "rec-schema", title: "Deploy full structured data", impact: "Unlocks rich results, map pack eligibility and AI answer citations. Highest impact single change.", priority: "high", effort: "Easy", horizon: "Quick win" },
        { id: "rec-canonical", title: "Add canonicals, robots.txt and social tags", impact: "Cleans crawl signals and makes shared or AI-previewed links render correctly.", priority: "high", effort: "Easy", horizon: "Quick win" },
        { id: "rec-titles", title: "Rewrite homepage title and fix duplicate labels", impact: "Captures head-term relevance on the pages already earning traffic.", priority: "high", effort: "Easy", horizon: "Quick win" },
        { id: "rec-faq", title: "FAQ markup on the existing FAQ page", impact: "Existing answers become quotable by AI engines without writing new content.", priority: "medium", effort: "Easy", horizon: "Quick win" },
        { id: "rec-linking", title: "Internal linking pass for orphan pages", impact: "Reconnects stranded pages so crawl paths and equity flow again.", priority: "medium", effort: "Easy", horizon: "Quick win" },
        { id: "rec-content", title: "Build an answer-first content hub", impact: "Wins the top of funnel queries competitors and listicles currently own.", priority: "medium", effort: "Medium", horizon: "Long term" },
        { id: "rec-images", title: "Convert images to WebP, lazy-load, add dimensions", impact: "Lifts Core Web Vitals and the mobile first impression.", priority: "low", effort: "Easy", horizon: "Quick win" },
        { id: "rec-gbp", title: "Optimize Google Business Profiles and NAP consistency", impact: "Reclaims the branded results page from aggregators.", priority: "medium", effort: "Hard", horizon: "Long term" },
        { id: "rec-reviews", title: "Seed review velocity program", impact: "Feeds the reputation signals both Google and AI engines weigh.", priority: "low", effort: "Medium", horizon: "Long term" },
        { id: "rec-og", title: "Open Graph and Twitter cards sitewide", impact: "Shared links finally carry brand imagery and copy.", priority: "low", effort: "Easy", horizon: "Quick win" },
    ];

    const competitors: CompetitorRow[] = snapshot.rivals.map((r, i) => ({
        name: r.name,
        domain: r.domain,
        aeo: Math.min(92, aeoScore + 12 + ((h + i * 53) % 26)),
        seo: Math.min(94, siteHealthScore + 6 + ((h + i * 29) % 22)),
        queries: r.winsQuestionIds.length,
        note: i === 0 ? "Owns the queries you are missing. Schema already deployed." : "Ranks via aggregator listicles more than its own site.",
    }));

    const rival1 = snapshot.rivals[0]?.name ?? "a competitor";
    const rival2 = snapshot.rivals[1]?.name ?? "aggregator listicles";
    const naming: BusinessAudit["naming"] = {
        capturedAt: snapshot.capturedAt,
        entries: [
            { engine: "ChatGPT", summary: `Knows ${business.name} exists but rarely names it first. For most ${svc} questions in ${business.city} it leads with ${rival1}.`, mentions: [rival1, rival2] },
            { engine: "Gemini", summary: `Names ${business.name} occasionally for branded queries, citing directory listings rather than the site itself.`, mentions: [rival1] },
        ],
    };

    // Core Web Vitals. Mobile is deliberately weaker than desktop (real sites
    // almost always are), which is what makes the "fix mobile first" story true.
    const cwvVerdict = (perf: number): CwvVerdict =>
        perf >= 90 ? "good" : perf >= 50 ? "needs-improvement" : "poor";
    const mobilePerf = Math.max(22, Math.min(68, 28 + (h % 34)));
    const desktopPerf = Math.min(99, mobilePerf + 24 + (h % 10));
    // Title and Lighthouse audit id, as `_opportunities()` sends them: the
    // card keys its plain-English line on the id.
    const OPP_POOL: Array<[label: string, id: string]> = [
        ["Properly size images", "uses-responsive-images"],
        ["Eliminate render-blocking resources", "render-blocking-resources"],
        ["Serve images in next-gen formats", "modern-image-formats"],
        ["Defer offscreen images", "offscreen-images"],
        ["Reduce unused JavaScript", "unused-javascript"],
        ["Enable text compression", "uses-text-compression"],
        ["Minify JavaScript", "unminified-javascript"],
    ];
    const oppsFor = (scale: number): PsOpportunity[] =>
        [0, 1, 2].map((i) => {
            const [label, id] = OPP_POOL[(h + i * 3) % OPP_POOL.length];
            return { label, id, savingMs: Math.round((2600 - i * 700) * scale + ((h + i * 11) % 5) * 100) };
        });
    const mobileLcp = 3200 + (h % 45) * 130;
    const mobileCls = Number((0.05 + (h % 22) / 100).toFixed(2));
    const mobileInp = 190 + (h % 20) * 22;
    const desktopLcp = 1200 + (h % 20) * 65;
    const desktopCls = Number((0.01 + (h % 9) / 100).toFixed(2));
    const desktopInp = 90 + (h % 15) * 8;
    // Lighthouse's other three categories, which PSI returns beside
    // Performance on every run. They barely move between devices, so the
    // desktop run is the phone's plus a small `lift`.
    const categories = (lift: number) => ({
        accessibility: Math.min(100, 82 + (h % 12) + lift),
        bestPractices: Math.min(100, 74 + (h % 18) + lift),
        seo: Math.min(100, 85 + (h % 10) + lift),
    });
    const pageSpeed: PageSpeed = {
        capturedAt: snapshot.capturedAt,
        runs: [
            { strategy: "mobile", performance: mobilePerf, lcpMs: mobileLcp, fcpMs: 1800 + (h % 20) * 70, cls: mobileCls, tbtMs: 220 + (h % 30) * 28, siMs: 4200 + (h % 30) * 110, inpMs: mobileInp, ttfbMs: 600 + (h % 20) * 45, ttiMs: 5200 + (h % 30) * 130, fieldData: cwvVerdict(mobilePerf), fieldPass: mobileLcp <= 2500 && mobileCls <= 0.1 && mobileInp <= 200, opportunities: oppsFor(1), samples: 3, opportunitiesTotal: 3 + (h % 6), ...categories(0) },
            { strategy: "desktop", performance: desktopPerf, lcpMs: desktopLcp, fcpMs: 700 + (h % 15) * 35, cls: desktopCls, tbtMs: 40 + (h % 15) * 12, siMs: 1500 + (h % 20) * 65, inpMs: desktopInp, ttfbMs: 200 + (h % 15) * 25, ttiMs: 1900 + (h % 20) * 70, fieldData: cwvVerdict(desktopPerf), fieldPass: desktopLcp <= 2500 && desktopCls <= 0.1 && desktopInp <= 200, opportunities: oppsFor(0.4), samples: 3, opportunitiesTotal: 3 + (h % 4), ...categories(3) },
        ],
        pages: priorityPages.slice(0, 4).map((p, i) => ({
            url: p.url,
            title: p.title,
            performance: Math.max(20, Math.min(96, mobilePerf - 4 + ((h + i * 17) % 22))),
            lcpMs: 2800 + ((h + i * 31) % 42) * 120,
        })),
        pagesTotal: priorityPages.length,
    };

    // The third gauge on fixtures (#4877, B6): five components, each over its
    // own denominator, and the mean of the ones that apply. Fixtures carried no
    // readability block at all, which rendered as "not measured" and left the
    // staff Overview unreviewable on sample data.
    const withHeading = pagesCrawled - 2 - (h % 3);
    const readabilityBase: Array<Omit<AeoReadabilityComponent, "score">> = [
        { key: "headings", label: "Pages with a heading", covered: withHeading, applicable: pagesCrawled },
        // The pages the structured-data finding does not list, so this card and
        // that finding count the same pages.
        { key: "entity_markup", label: "Pages with entity markup", covered: pagesCrawled - issueCount("no_schema"), applicable: pagesCrawled },
        { key: "prices", label: "Treatment pages with a price", covered: h % 2, applicable: 8 + (h % 6) },
        { key: "staff", label: "Staff pages linked to the business", covered: 0, applicable: 3 + (h % 4) },
        { key: "branches", label: "Branch records found", covered: 1, applicable: 1 + (h % 2) },
    ];
    const readabilityComponents: AeoReadabilityComponent[] = readabilityBase.map(
        (c) => ({ ...c, score: Math.round((c.covered / c.applicable) * 100) }),
    );
    const aiReadabilityScore = Math.round(
        readabilityComponents.reduce((n, c) => n + c.score, 0) / readabilityComponents.length,
    );
    const aiReadabilityVerdict =
        aiReadabilityScore >= 75 ? "Strong AI readability, keep protecting it"
            : aiReadabilityScore >= 50 ? "Developing AI readability, clear upside"
                : aiReadabilityScore >= 25 ? "Weak AI readability, high upside"
                    : "Critical gap in AI readability, major upside";

    // Health by area from the same fixture crawl, by the backend's rules:
    // markup counts as present only when entity markup covers half the pages.
    const entityMarkup = readabilityComponents.find((c) => c.key === "entity_markup");
    const healthByArea: BusinessAudit["healthByArea"] = {
        crawlability: Math.round((100 * (pagesCrawled - issueCount("broken_4xx"))) / pagesCrawled),
        internalLinking: Math.round((100 * (pagesCrawled - issueCount("orphan_pages"))) / pagesCrawled),
        markup: entityMarkup && entityMarkup.covered * 2 >= entityMarkup.applicable ? "present" : "missing",
        robotsTxt: issueCount("robots_missing") > 0 ? "not_found" : "found",
    };

    const weaknesses = weaknessesOf(issues);
    // fixture-only: a live strength is a check that found nothing
    // (`_strengths`), and every sample check found something, so the sample
    // states its own passing counts: the numbers Health by area and What a
    // machine can read show for the same crawl. Title and line, split at the
    // spaced dash, as the backend's strength copy is.
    const branches = readabilityComponents.find((c) => c.key === "branches");
    const strengths = [
        `An H1 heading on ${withHeading} of ${pagesCrawled} pages \u2013 A clear title a machine can quote.`,
        `${pagesCrawled - issueCount("broken_4xx")} of ${pagesCrawled} pages load without an error \u2013 Visitors and crawlers reach the page itself.`,
        `${pagesCrawled - issueCount("orphan_pages")} of ${pagesCrawled} pages linked from the rest of the site \u2013 Nothing there depends on luck to be found.`,
        ...(branches && branches.covered === branches.applicable
            ? ["A branch record for every location \u2013 The address AI matches to the map."]
            : []),
    ];

    // The crawl inventory (#3957), as a live scan sends it: every page read,
    // each with the checks it failed. Every page the sample names (its
    // findings, key pages, weak pages and Search Console pages on this
    // domain), topped up with posts to the crawl's count, so "32 pages read",
    // the Site pages list and the Page workshop describe the same pages.
    const site = pageUrlKey(d);
    const inventory = new Map<string, { url: string; title: string }>();
    const addPage = (url: string, title?: string) => {
        const key = pageUrlKey(url);
        if (!inventory.has(key)) inventory.set(key, { url, title: title || titleFromPath(key.slice(site.length) || "/") });
    };
    CRAWL_PATHS.forEach((p) => addPage(`${d}${p}`));
    priorityPages.forEach((p) => addPage(p.url, p.title));
    underperforming.forEach((p) => addPage(p.url, p.title));
    (search.pages ?? []).forEach((p) => {
        const key = pageUrlKey(p.url);
        if (key === site || key.startsWith(`${site}/`)) addPage(p.url);
    });
    for (let n = 1; inventory.size < pagesCrawled; n++) addPage(`${d}/blog/post-${n}`);
    const crawledPages: CrawledPageRow[] = Array.from(inventory, ([key, page]) => ({
        ...page,
        failed: issues.filter((i) => i.pages.some((pg) => pageUrlKey(pg.url) === key)).map((i) => i.key),
    }));

    return {
        siteHealthScore,
        aeoScore,
        aiReadabilityScore,
        aiReadabilityVerdict,
        aiReadabilityComponents: readabilityComponents,
        // fixture-only: the crawl behind the readability score, as a live scan
        // sends it (ourteam-backend#4995), so the sample shows the read-coverage gate.
        aiReadabilityCrawl: { pagesCrawled, readablePages: pagesCrawled - 1, readableShare: (pagesCrawled - 1) / pagesCrawled, readableFloor: 0.7 },
        siteHealthVerdict: siteHealthVerdictFor(siteHealthScore),
        aeoVerdict: verdictFor(aeoScore),
        siteHealth,
        pagesCrawled,
        errors,
        warnings,
        issues,
        strengths,
        weaknesses,
        priorityPages,
        underperforming,
        traffic,
        recommendations,
        competitors,
        naming,
        pageSpeed,
        healthByArea,
        crawledPages,
        crawledAt: snapshot.capturedAt,
        // fixture-only until ourteam-backend handoff: Site Health method stamp for deltas
        previousSiteHealth: { score: Math.max(0, siteHealthScore - 7), at: daysBefore(snapshot.capturedAt, 29), sameMethod: true },
    };
}

// ---------------------------------------------------------------------------
// AI visibility rollup: the score, mention metrics, distribution by LLM and
// mentions by country. Shaped like the future aiVisibility block in the
// assessment payload; today derived deterministically from the probe results.

export interface EngineShare {
    engine: "ChatGPT" | "Gemini" | "Google AI";
    mentions: number;
    sharePct: number;
    /** False when the engine was never asked. Absent on fixtures, which probe
     *  everything they list. */
    probed?: boolean;
    summary: string; // how this engine describes the business today (naming baseline)
    rivals: string[]; // who it names instead
    /** Which instrument produced the row. `"google_serp"` is the AI Overviews
     *  read our own browser performed (#3922). */
    instrument?: string;
    skipReason?: string;
    skipDetail?: string;
    /** Answers the engine actually produced, when that differs from the
     *  question count — Google renders an overview for only some searches. */
    answered?: number | null;
    sharePctBasis?: string | null;
    /** `answered + notShown + failed === asked`, always. */
    coverage?: { asked: number; answered: number; notShown: number; failed: number } | null;
    /** When THIS row was read. A refresh moves it without moving the scan. */
    capturedAt?: string | null;
    country?: string | null;
}

export interface CountryShare {
    country: string;
    flag: string;
    mentions: number;
    sharePct: number;
}

export interface AiVisibility {
    score: number | null; // 0-100, the AEO score; null when the scan could not produce one
    verdict: "High" | "Medium" | "Low";
    blurb: string;
    capturedAt: string;
    /** Engine answers naming the business, ADDED across engines: a question two
     *  engines named counts twice. A count, never a rate — see questionsNamed. */
    mentions: number;
    /** Questions of the set at least one engine named, and what that is out of
     *  (ourteam-backend#4976). The number the app shows; absent on older scans. */
    questionsNamed?: number;
    questionsOf?: number;
    /** The own-site citation share the backend divides itself (#4868). */
    citationShare?: { count: number; denominator: number; share: number; absent?: string | null } | null;
    /** `null` = no prior scan to compare with (never a measured 0%). */
    mentionsDeltaPct: number | null;
    /** `null` when the instrument cannot see citations (`citationsAbsent`). */
    citations: number | null;
    citationsDeltaPct: number | null;
    citationsAbsent?: string | null;
    /** Which engines the citation figure was read from (#3889). Absent on older
     *  scans, where the figure simply has no stated source. */
    citationsFrom?: string[] | null;
    citedPages: number;
    citedPagesDeltaPct: number | null;
    /** What the score was divided by. Live only. */
    coverage?: AeoVisibilityCoverage;
    /** Per-engine hit rates, keyed by engine id (#3684). The pooled assistants
     *  figure is computed from these, never by adding percentages. */
    engineRates?: Record<string, { mentioned: number; answered: number }>;
    /** Sites the answers quote, ranked (#3788). Live only; absent on older scans. */
    sourceDomains?: AeoSourceDomain[];
    sourceDomainsTotal?: number;
    /** What `sourceDomains[].answers` counts: `"questions"` since
     *  ourteam-backend#5029, answers before that (#3890). */
    sourceDomainsBasis?: string | null;
    ownDomainAnswers?: number | null;
    trend: number[]; // mentions, one point per assessment (see trendBasis)
    /** `"per_assessment"` — one point is a scan, not a week (#4354). Absent on
     *  older runs, which is why nothing here assumes weeks. */
    trendBasis?: string;
    /** When each trend point was captured, index-aligned with `trend`. Entries
     *  can be null on points stored before dates were kept. Locating a trend
     *  break needs these (#3539). */
    trendCapturedAt?: (string | null)[];
    engines: EngineShare[];
    countries: CountryShare[];
    /**
     * The four checks of the v3 Visibility Set, each as a count (#3538).
     *
     * Absent on a pre-v3 scan — the BE returns nothing rather than four zeros
     * so the screen can say "not measured on this scan" instead of showing
     * what looks like four failures.
     */
    checks?: Partial<Record<VisibilityCheckName, VisibilityCheck>>;
    /** Which questions each rival was named on (ourteam-backend#3803).
     *  Optional: absent on runs finalized before that shipped. */
    competitorWins?: {
        name: string;
        /** Every question the rival is named on, the business's own included: it cannot say "takes N". */
        queryCount: number;
        queries: string[];
        /**
         * The questions the rival was named on and the business was NOT, and how
         * many (ourteam-backend#5124). The only fields that can say a rival took
         * an answer. Absent on a scan from before they were sent, which is not 0.
         */
        insteadCount?: number;
        insteadQueries?: string[];
    }[];
}

/**
 * The checks, in the spec's own order. A local scan (Visibility Set v4.2) has
 * location, specialty, intent and authority: 26, 12, 8 and 4 questions. A brand
 * scan (v4.5, ourteam-backend#5883) has pick, source and supplier: 28, 14 and 8.
 * A scan carries one family or the other, never both.
 */
export type VisibilityCheckName =
    | "location" | "specialty" | "intent" | "authority"
    | "pick" | "source" | "supplier";

/**
 * One check's result. A COUNT, never a percentage.
 *
 * "You scored 34%" gives a client nothing to buy — one blended number does not
 * say whether they are losing on location, on services, on buying moments or
 * on authority, which are four different jobs at four different prices. And a
 * four-question check cannot carry a percentage: "1 of 4" is honest where
 * "25%" implies a precision that is not there.
 */
export interface VisibilityCheck {
    mentioned: number;
    answered: number;
    /** `"8 of 26 answers"` / `"not measured"`, pre-formatted by the BE. Older
     *  scans send a bare `"8 of 26"`; see `unit`. */
    label: string;
    /** `"answers"` from ourteam-backend#4934 on — and `label` then already
     *  carries it ("5 of 28 answers"). Absent on older scans, whose label is a
     *  bare "5 of 28". */
    unit?: "answers";
    /** Question counts for the SAME check (#4934), the unit a group header
     *  shows. `label` counts answers (one per engine per question); these count
     *  questions. Absent on older scans. */
    questions?: number;
    questionsAnswered?: number;
    questionsNamed?: number;
    /** Questions no scored answer came from — unwinnable, or every answer
     *  withheld. Said out loud so the gap reads as rigour, not as missing rows. */
    questionsExcluded?: number;

    /** What this check asks, in the client's words. */
    question: string;
    /** The work that closes this gap. */
    fix: string;
}

/**
 * The four checks in the order the spec lists them, which is also descending
 * question count (26 · 12 · 8 · 4). Fixed here rather than taken from
 * `Object.keys(checks)` so the sections do not reorder between two scans.
 *
 * The brand checks follow the local ones. Callers list only the kinds the
 * scan's rows carry (`kindsOf`), so a local scan shows its four and a brand
 * scan its three, with nothing for the family it does not have.
 */
export const VISIBILITY_CHECK_ORDER: readonly VisibilityCheckName[] = [
    "location",
    "specialty",
    "intent",
    "authority",
    "pick",
    "source",
    "supplier",
];

export function getAiVisibility(business: Business, snapshot: ScoreSnapshot): AiVisibility {
    const h = hashOf(business.id);
    const audit = getAudit(business, snapshot);
    // Fixtures always carry a number; the live path never reaches this generator.
    const score = audit.aeoScore ?? 0;

    const verdict: AiVisibility["verdict"] = score >= 70 ? "High" : score >= 40 ? "Medium" : "Low";
    const blurb =
        verdict === "High"
            ? "Consistently named in AI answers. Defend the position."
            : verdict === "Medium"
                ? "Occasionally mentioned in AI outputs. Visibility can improve."
                : "Rarely surfaces in AI answers today. High upside once the fixes land.";

    const mentions = 60 + (h % 180);
    const chatgptShare = 30 + (h % 18);
    const geminiShare = 24 + (h % 14);
    const googleShare = Math.max(8, 100 - chatgptShare - geminiShare);
    const rival1 = snapshot.rivals[0]?.name ?? "a competitor";
    const rival2 = snapshot.rivals[1]?.name ?? "aggregator listicles";

    const engines: EngineShare[] = [
        {
            engine: "ChatGPT",
            mentions: Math.round((mentions * chatgptShare) / 100),
            sharePct: chatgptShare,
            summary: audit.naming.entries[0]?.summary ?? "",
            rivals: audit.naming.entries[0]?.mentions ?? [rival1],
        },
        {
            engine: "Gemini",
            mentions: Math.round((mentions * geminiShare) / 100),
            sharePct: geminiShare,
            summary: audit.naming.entries[1]?.summary ?? "",
            rivals: audit.naming.entries[1]?.mentions ?? [rival1],
        },
        {
            engine: "Google AI",
            mentions: Math.round((mentions * googleShare) / 100),
            sharePct: googleShare,
            summary: `Shows ${business.name} in AI Overviews for branded searches only. The local pack feeds it, so profile work moves this fastest.`,
            rivals: [rival2],
        },
    ];

    const myShare = 52 + (h % 20);
    const sgShare = 12 + (h % 10);
    const usShare = 6 + (h % 8);
    const countries: CountryShare[] = [
        { country: "Malaysia", flag: "🇲🇾", mentions: Math.round((mentions * myShare) / 100), sharePct: myShare },
        { country: "Singapore", flag: "🇸🇬", mentions: Math.round((mentions * sgShare) / 100), sharePct: sgShare },
        { country: "United States", flag: "🇺🇸", mentions: Math.round((mentions * usShare) / 100), sharePct: usShare },
        { country: "Other", flag: "🌐", mentions: Math.round((mentions * (100 - myShare - sgShare - usShare)) / 100), sharePct: 100 - myShare - sgShare - usShare },
    ];

    const trend = Array.from({ length: 12 }, (_, i) =>
        Math.max(2, Math.round(mentions / 14 + (i * mentions) / 160 + (((h + i * 29) % 11) - 5))),
    );

    return {
        score,
        verdict,
        blurb,
        capturedAt: snapshot.capturedAt,
        mentions,
        mentionsDeltaPct: -6 + (h % 21),
        citations: Math.round(mentions * 1.7) + (h % 20),
        citationsDeltaPct: 2 + (h % 12),
        citedPages: 4 + (h % 9),
        citedPagesDeltaPct: 1 + (h % 9),
        trend,
        engines,
        countries,
    };
}

// ---------------------------------------------------------------------------
// The 50-question set (tracker #5 measures movement against these answers).
// Fixture questions come first; the rest are generated deterministically from
// the business's vertical and city, the same way the real query author works.

const MODIFIERS = ["best", "affordable", "top rated", "recommended", "trusted", "good"];
const SUFFIXES = ["near me", "open on weekends", "with parking", "for beginners", "price list", "reviews"];

export function getFiftyQuestions(business: Business, snapshot: ScoreSnapshot): QuestionResult[] {
    const h = hashOf(business.id);
    const out: QuestionResult[] = [...snapshot.questions];
    // Oura supports ChatGPT and Gemini only for now.
    const engines: QuestionResult["engines"][number]["engine"][] = ["chatgpt", "gemini"];
    let i = 0;
    while (out.length < 50) {
        const mod = MODIFIERS[(h + i) % MODIFIERS.length];
        const suf = SUFFIXES[(h + i * 3) % SUFFIXES.length];
        const where = i % 2 === 0 ? business.city : `${business.city} ${suf}`;
        const text = i % 3 === 0
            ? `${mod} ${business.vertical.toLowerCase()} in ${where}`
            : `${business.vertical.toLowerCase()} ${suf} ${business.city}`;
        const seed = h + i * 101;
        out.push({
            id: `gq-${i}`,
            text,
            engines: engines.map((engine, e) => {
                const hits = Math.max(0, ((seed + e * 13) % 7) - 4); // mostly 0, some 1-2
                return { engine, samples: 3, hits, position: hits > 0 ? 1 + ((seed + e) % 3) : null };
            }),
            googleOrganic: (seed % 4 === 0) ? null : 3 + (seed % 17),
            localPack: (seed % 3 === 0) ? 1 + (seed % 3) : null,
            siteAnswers: seed % 3 !== 0,
            actionId: null,
        });
        i += 1;
    }
    return out.slice(0, 50);
}
